import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ConfirmDialog } from './ConfirmDialog'
import type { ConfirmDialogOptions } from './ConfirmDialog'
import { InputDialog } from './InputDialog'
import type { InputDialogOptions } from './InputDialog'

type ConfirmState = (ConfirmDialogOptions & { open: boolean }) | null
type InputState = (InputDialogOptions & { open: boolean }) | null

interface DialogContextValue {
  /** Promise-based confirm — resolves true on confirm, false on cancel/dismiss. */
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>
  /**
   * Promise-based prompt — resolves the field values object on submit, or null on
   * cancel/dismiss. Supports one or many fields (recipient/OTP/notes, etc.).
   */
  prompt: (options: InputDialogOptions) => Promise<Record<string, string> | null>
}

const DialogContext = createContext<DialogContextValue | null>(null)

/**
 * Hosts one ConfirmDialog + one InputDialog for the subtree and exposes imperative,
 * promise-based `useConfirm()` / `useInputDialog()` hooks (KAN-1179). Mount inside
 * each portal's provider tree (alongside ToastProvider) so any descendant can retire
 * `window.confirm` / `window.prompt`.
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState>(null)
  const [inputState, setInputState] = useState<InputState>(null)
  const confirmResolver = useRef<((result: boolean) => void) | null>(null)
  const inputResolver = useRef<((result: Record<string, string> | null) => void) | null>(null)

  const confirm = useCallback((options: ConfirmDialogOptions) => {
    return new Promise<boolean>(resolve => {
      confirmResolver.current = resolve
      setConfirmState({ ...options, open: true })
    })
  }, [])

  const prompt = useCallback((options: InputDialogOptions) => {
    return new Promise<Record<string, string> | null>(resolve => {
      inputResolver.current = resolve
      setInputState({ ...options, open: true })
    })
  }, [])

  const settleConfirm = useCallback((result: boolean) => {
    confirmResolver.current?.(result)
    confirmResolver.current = null
    setConfirmState(current => (current ? { ...current, open: false } : null))
  }, [])

  const settleInput = useCallback((result: Record<string, string> | null) => {
    inputResolver.current?.(result)
    inputResolver.current = null
    setInputState(current => (current ? { ...current, open: false } : null))
  }, [])

  const value = useMemo<DialogContextValue>(() => ({ confirm, prompt }), [confirm, prompt])

  return (
    <DialogContext.Provider value={value}>
      {children}
      {confirmState && (
        <ConfirmDialog
          {...confirmState}
          onConfirm={() => settleConfirm(true)}
          onCancel={() => settleConfirm(false)}
        />
      )}
      {inputState && (
        <InputDialog
          {...inputState}
          onSubmit={values => settleInput(values)}
          onCancel={() => settleInput(null)}
        />
      )}
    </DialogContext.Provider>
  )
}

function useDialogContext(): DialogContextValue {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useConfirm/useInputDialog must be used within a DialogProvider')
  }
  return context
}

/** Promise-based confirm dialog. `const ok = await confirm({ title, message })`. */
export function useConfirm(): (options: ConfirmDialogOptions) => Promise<boolean> {
  return useDialogContext().confirm
}

/**
 * Promise-based prompt dialog. Resolves the field values object, or null on cancel.
 * `const values = await inputDialog({ title, fields: [...] })`.
 */
export function useInputDialog(): (options: InputDialogOptions) => Promise<Record<string, string> | null> {
  return useDialogContext().prompt
}
